<template>
  <v-dialog v-model="computedDialog" max-width="900px" @keydown.esc="close">
    <v-card v-draggable class="pa-1">
      <v-card-title>
        <span class="text-h5">
          {{ formTitle }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-container grid-list-lg>
          <v-row dense wrap>
            <v-col cols="12">
              <v-autocomplete
                density="compact"
                :items="allBrandsComputed"
                item-title="Brand_Name"
                item-value="Brand_SK"
                v-model="selectedBrandSk"
                label="Brand"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" class="d-flex gap-2">
              <div style="flex: 1;">
                <v-autocomplete
                  density="compact"
                  :disabled="!selectedBrandSk"
                  :items="linesFromSelectedBrand"
                  item-title="Line_Name"
                  item-value="Line_SK"
                  v-model="editedItem.Line_FK"
                  label="Line"
                ></v-autocomplete>
              </div>
              <v-btn
                :disabled="!selectedBrandSk || !firstGenericLine"
                color="secondary"
                variant="tonal"
                size="default"
                style="align-self: flex-start; margin-top: 4px;"
                @click="selectGenericLine"
                title="Select generic line"
                prepend-icon="mdi-auto-fix"
              >
                Generic
              </v-btn>
              <v-btn
                v-if="selectedBrandSk"
                color="primary"
                variant="tonal"
                size="default"
                style="align-self: flex-start; margin-top: 4px;"
                @click="showAddLineDialog = true"
                title="Create new line"
                prepend-icon="mdi-plus"
              >
                Add Line
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-text-field
                density="compact"
                ref="productName"
                v-model="editedItem.Product_Name"
                label="Product Name"
                :hint="originalProductName ? `Base Product Name: '${originalProductName}'` : null"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row dense wrap>
            <v-col cols="12">
              <v-autocomplete
                density="compact"
                :items="allCategoriesComputed"
                item-title="Category_Name"
                item-value="Category_SK"
                v-model="editedItem.Category_FK"
                label="Category"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                density="compact"
                :items="allBenefitsComputed"
                item-title="Benefit_Name"
                item-value="Benefit_SK"
                v-model="editedItem.Benefit_FK"
                label="Benefit"
              ></v-autocomplete>
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                density="compact"
                :items="allMultiPacksComputed"
                item-title="Multi_Pack_Name"
                item-value="Multi_Pack_SK"
                v-model="editedItem.Multi_Pack_FK"
                label="Multi-Pack"
              ></v-autocomplete>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                label="TREX"
                v-model="editedItem.TREX"
                density="compact"></v-checkbox>
            </v-col>
<!--             <v-col cols="4">
              <v-checkbox
                label="Discard"
                color="red"
                v-model="editedItem.Discard"
                density="compact"></v-checkbox>
            </v-col> -->
            
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!mandatoryFieldsPopulated || loading"
          :loading="loading"
          @click="save"
          class="px-5 bg-blue"
          prepend-icon="mdi-content-save-outline"
        >
          Save
        </v-btn>
        <v-btn
          class="px-4"
          variant="text"
          @click="close()"
          prepend-icon="mdi-close"
        >
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <AddLineDialog
    :dialog="showAddLineDialog"
    :brandFk="selectedBrandSk"
    @update:dialog="showAddLineDialog = $event"
    @line-created="onLineCreated"
  />
</template>

<script>
import { inject } from 'vue'
import { mapState, mapMutations } from 'vuex'
import ADD_PRODUCT from '@/graphql/Product/addProduct.gql'
import UPDATE_PRODUCT from '@/graphql/Product/updateProduct.gql'
import AddLineDialog from '@/components/AddLineDialog.vue'

export default {
  name: 'MasterProductDialog',
  components: {
    AddLineDialog
  },
  emits: ['closeDialog'],
  setup() {
    return {
      rc: inject('rc')
    }
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    masterProduct: {
      type: Object,
      default: () => ({}),
      required: false
    },
    baseProductSk: {
      type: Number,
      required: false
    },
    baseProductMatchingSk: {
      type: Number,
      required: false
    }
  },
  data () {
    return {
      loading: false,
      editedItem: {},
      originalProductName: '',
      selectedBrandSk: null,
      showAddLineDialog: false
    }
  },
  watch: {
    dialog(val) {
      val || this.close()
    },
    selectedBrandSk(newVal, oldVal) {
      // When brand is changed, clear the line selection
      if (newVal !== oldVal && oldVal !== null) {
        this.editedItem.Line_FK = null
      }
    },
    dialogMode(val) {
      if (val == 'add-master-product-based-on-product') {
        this.selectedBrandSk = this.baseProduct?.Brand_FK
        this.editedItem = {
          ...this.baseProduct,
          Product_SK: undefined
        }
      } else if (val == 'add-master-product-based-on-matching') {
        this.selectedBrandSk = this.baseMismatch?.Suggested_Brand_FK
        this.editedItem = {
          Line_FK: this.baseMismatch?.Suggested_Line_FK,
          Category_FK: this.baseMismatch?.Suggested_Category_FK,
          Benefit_FK: this.baseMismatch?.Suggested_Benefit_FK,
          Multi_Pack_FK: this.baseMismatch?.Suggested_Multi_Pack_FK,
          TREX: this.baseMismatch?.Suggested_TREX,
          // Discard: this.baseMismatch?.Suggested_Discard,
        }
      } else if (val == 'edit-master-product') {
        this.selectedBrandSk = this.masterProduct?.Brand_FK
        this.editedItem = this.masterProduct
      } else { // add-master-product-from-scratch
        this.selectedBrandSk = null
        this.editedItem = {}
      }
    }
  },
  methods: {
    ...mapMutations(['addError', 'setCurrentProductSk']),
    selectGenericLine() {
      if (this.firstGenericLine) {
        this.editedItem.Line_FK = this.firstGenericLine.Line_SK
      }
    },
    onLineCreated(newLine) {
      // Auto-select the newly created line
      this.editedItem.Line_FK = newLine.Line_SK
      this.showAddLineDialog = false
    },
    save() {
      this.loading = true
      if (this.dialogMode === 'edit-master-product') {
        this.$apollo.mutate({
          mutation: UPDATE_PRODUCT,
          variables: {
            UpdateProductInput: Object.assign({}, this.editedItem, {
              __typename: undefined,
              Line_Name: undefined,
              Brand_FK: undefined,
              Brand_Name: undefined,
              Participant_FK: undefined,
              House_FK: undefined,
              House_Name: undefined,
              Company_FK: undefined,
              Company_Name: undefined,
              Category_Name: undefined,
              Multi_Pack_Name: undefined,
              Benefit_Name: undefined,
              Total_Amount_USD: undefined,
              Mutation_Logs_Count: undefined
            })
          },
          update: (cache, { data: { updateProduct } }) => {
            cache.modify({
              id: `Product:${updateProduct.Product_SK}`,
              fields: {
                Mutation_Logs_Count(count) {
                  return (count || 0) + 1
                }
              }
            })
          }
        })
          .then(() => {
            this.close()
          })
          .catch((error) => this.addError(error))
          .finally(() => this.loading = false)
      } else {
      // Add (either add-master-product-from-scratch or add-master-product-based-on-matching or add-master-product-based-on-product)
      this.$apollo.mutate({
        mutation: ADD_PRODUCT,
        variables: {
          AddProductInput: Object.assign({}, this.editedItem, {
            // the below list is probably not useful?
            __typename: undefined,
            Line_Name: undefined,
            Brand_FK: undefined,
            Brand_Name: undefined,
            Participant_FK: undefined,
            House_FK: undefined,
            House_Name: undefined,
            Company_FK: undefined,
            Company_Name: undefined,
            Category_Name: undefined,
            Multi_Pack_Name: undefined,
            Benefit_Name: undefined,
            Total_Amount_USD: undefined,
            Mutation_Logs_Count: undefined
          })
        },
        update: (cache, { data: { addProduct } }) => {
          // Add new product to the cached allProducts array (works regardless of query variables)
          cache.modify({
            fields: {
              allProducts(existingProducts = [], { toReference }) {
                const newProductRef = toReference(addProduct)
                return [...existingProducts, newProductRef]
              }
            }
          })
          // Set initial mutation log count on the new product
          cache.modify({
            id: `Product:${addProduct.Product_SK}`,
            fields: {
              Mutation_Logs_Count() {
                return 1
              }
            }
          })
        }
      })
      .then(({ data }) => {
        if (this.variation) {
          this.setCurrentProductSk(data.addProduct.Product_SK)
        }
        this.close()
      })
        .catch((error) => this.addError(error))
        .finally(() => this.loading = false)
      }
    },
    close() {
      this.editedItem = Object.assign({})
      this.loading = false
      this.$emit('closeDialog')
      // should I reset the mode here? in case the next one is relaunched with the same mode?
      // probably not 
    }
  },
  computed: {
    ...mapState(['currentProductMismatchesSks']),
    currentMasterProductName() {
      return this.editedItem?.Master_Product_Name
    },
    mandatoryFieldsPopulated() {
      return Boolean(
        this.editedItem?.Line_FK && 
        this.editedItem?.Product_Name &&
        this.editedItem?.Category_FK
        )
    },
    computedDialog: {
      get() {
        return this.dialog
      },
      set(_value) {
        this.close()
      }
    },
    selectedTags: {
      get() {
        return this.editedItem?.Tags?.map(t => t.Tag)
      },
      set(value) {
        this.editedItem.Tags = value.map(tag => ({ Tag: tag }))
      }
    },
    linesFromSelectedBrand() {
      return this.selectedBrandSk
        ? this.rc?.allLines?.filter(line => line.Brand_FK === this.selectedBrandSk) || []
        : this.rc?.allLines || []
    },
    firstGenericLine() {
      return this.linesFromSelectedBrand?.find(line => line.Line_Name === 'Generic Line') || null
    },
    baseProduct () {
      return this.rc?.allProducts?.find(prod => prod.Product_SK === this.baseProductSk)
    },
    baseMismatch () {
      return this.rc?.allProductMatchings?.find(pm => pm.Product_Matching_SK === this.baseProductMatchingSk)
    },
    dialogMode() {
      if (this.baseProductSk) {
        return 'add-master-product-based-on-product'
      } else if (this.baseProductMatchingSk) {
        return 'add-master-product-based-on-matching'
      } else if (this.masterProduct?.Product_SK) {
        return 'edit-master-product'
      } else {
        return 'add-master-product-from-scratch'
      }
    },
    formTitle() {
      if (this.dialogMode === 'add-master-product-based-on-product') {
        return 'New Master Product based on another Product'
      } else if (this.dialogMode === 'add-master-product-based-on-matching') {
        return 'New Master Product based on Suggestions'
      } else if (this.dialogMode === 'edit-master-product') {
        return 'Edit Master Product'
      } else {
        return 'New Master Product'
      }
    },
    allBrandsComputed() {
      return this.rc?.allBrands || []
    },
    allCategoriesComputed() {
      return this.rc?.allCategories || []
    },
    allBenefitsComputed() {
      return this.rc?.allBenefits || []
    },
    allMultiPacksComputed() {
      return this.rc?.allMultiPacks || []
    },
  }
}
</script>
<style scoped>
.white-border {
  border: 1px solid white;
}
.full-opacity {
  opacity: 1 !important;
}
</style>
